<template>
  <div>
    <div class="d-flex align-center mt-2">
      <v-sheet color="grey-8" height="60" rounded width="100%" class="d-flex align-center px-2">
        <div class="d-flex" style="width: 400px; max-width: 70%;">
          <v-text-field
            v-model="search"
            v-on:keyup.enter="searchMembers()"
            single-line
            hide-details
            outlined
            dense
            clearable
            placeholder="輸入姓名/電話號碼"
          />
        </div>
        <v-btn icon color="primary" class="ml-2" @click="searchMembers()">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <v-spacer/>
        <v-btn :icon="$vuetify.breakpoint.xsOnly" outlined @click="exportMembers">
          <v-icon>mdi-file-export</v-icon>
          <template v-if="$vuetify.breakpoint.smAndUp">匯出</template>
        </v-btn>
      </v-sheet>
    </div>

    <v-data-table
      :headers="headers"
      :items="members"
      :options.sync="options"
      :server-items-length="totalMembers"
      :footer-props="{itemsPerPageOptions: [10, 20, 50]}"
      :loading="loading"
      fixed-header
      height="calc(100vh - 380px)"
    >
      <template v-slot:item.name="{ item }">
        <div class="d-flex align-center" :class="{'ma-auto': $vuetify.breakpoint.smAndUp}" style="width: 122px;">
          <v-spacer v-if="$vuetify.breakpoint.xsOnly"/>
          <v-avatar size="36">
            <img
              :src="item.photo"
              :alt="item.name"
              @error="item.photo = 'https://dentco.s3.us-east-2.amazonaws.com/asset/crm/avatar-unknow.png'"
            >
          </v-avatar>
          <div class="ml-2">
            {{ item.name }}
          </div>
        </div>
      </template>
      <template v-slot:item.sex="{ item }">
        {{ item.sex | getGender }}
      </template>
      <template v-slot:item.birthday="{ item }">
        <template v-if="item.birthday">{{ parseInt(item.birthday) * 1000 | formatROCDate }}</template>
      </template>
      <template v-slot:item.bind_status="{ item }">
        <v-tooltip right max-width="500">
          <template v-slot:activator="{ on, attrs }">
            <v-img
              :src="showBindStatus({key: memberType, status: item.bind_status}).icon"
              max-width="24"
              width="24"
              class="ma-auto"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <span v-html="showBindStatus({key: memberType, status: item.bind_status, name: getName}).text"/>
        </v-tooltip>
      </template>
      <template v-slot:item.send="{ item }">
        <v-menu offset-x left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              small
              v-bind="attrs"
              @click="selectPatient = item; getSendChannelInfo($event, on)"
            >
              傳送訊息
            </v-btn>
          </template>
          <v-list dense outlined class="text-body-2">
            <v-list-item
              class="justify-center"
              @click="$refs.SendLineMessageV2.sendMessageDialog = true"
            >
              LINE訊息{{ sendLineSuffix }}
            </v-list-item>
            <v-list-item
              class="justify-center"
              @click="$refs.smsMessageV2.sendMessageDialog = true"
            >
              簡訊{{ sendSmsSuffix }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <SendLineMessageV2
      ref="SendLineMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="{
        patientName: selectPatient?.name,
        patientPhone: selectPatient?.phone,
        bindStatus: Number(selectPatient?.bind_status)
      }"
      style="display: none;"
    />
    <SendSmsMessageV2
      v-show="false"
      ref="smsMessageV2"
      :btnProps="{text: true}"
      :patient="{
        patientName: selectPatient?.name,
        patientPhone: selectPatient?.phone
      }"
    />
  </div>
</template>

<script>
import {
  exportLineMembersAPI,
  exportMessengerMembersAPI, getChannelQuotaAPI,
  getLineMembersAPI, getMessageSettingAPI,
  getMessengerMembersAPI, getPatientChannelBindStatusAPI, getSMSQuotaChannelInfoAPI
} from '../../api';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { bindStatus } from '../../config/bindConfig';
import { assetURL } from '../../utils/filters';
import SendLineMessageV2 from "@/components/utils/SendLineMessageV2.vue";
import SendSmsMessageV2 from "@/components/utils/SendSmsMessageV2.vue";

export default {
  name: "MemberList",
  components: {SendSmsMessageV2, SendLineMessageV2},

  props: {
    memberType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      search: null,
      members: [],
      options: {},
      totalMembers: 0,

      selectPatient: {},
      sendLineSuffix: '',
      sendSmsSuffix: ''
    }
  },

  computed: {
    ...mapState({
      user: state => state.user
    }),
    ...mapGetters([
      'axiosParamData',
      'ownChannelLoaded',
      'hasOwnChannel',
      'clinicUser',
      'doctorUser'
    ]),
    headers() {
      if (this.memberType === 'line') {
        return [
          {text: this.$t('Patients.patientName'), align: 'center', value: 'name', sortable: false, width: 120},
          {text: this.$t('Patients.patientPhone'), align: 'center', value: 'phone', sortable: true, width: 120},
          {text: 'Email', align: 'center', value: 'email', sortable: true, width: 120},
          {text: 'UID', align: 'center', value: 'uid', sortable: false, width: 120},
          {text: '語系', align: 'center', value: 'lang', sortable: true, width: 120},
          {text: this.$t('Patients.bindStatus'), align: 'center', value: 'bind_status', sortable: true, width: 80},
          {text: '傳送訊息', align: 'center', value: 'send', sortable: false, width: 150},
          {text: '加入來源', align: 'center', value: 'inviter', sortable: false, width: 80},
          {text: '加入時間', align: 'center', value: 'create_time', sortable: true, width: 80},
        ]
      } else {
        return [
          {text: this.$t('Patients.patientName'), align: 'center', value: 'name', sortable: false, width: 120},
          {text: this.$t('Patients.patientPhone'), align: 'center', value: 'phone', sortable: true, width: 120},
          {text: 'Email', align: 'center', value: 'email', sortable: true, width: 120},
          {text: 'UID', align: 'center', value: 'uid', sortable: false, width: 120},
          {text: '語系', align: 'center', value: 'lang', sortable: true, width: 120},
          {text: this.$t('Patients.bindStatus'), align: 'center', value: 'bind_status', sortable: true, width: 80},
          {text: '加入來源', align: 'center', value: 'inviter', sortable: false, width: 80},
          {text: '加入時間', align: 'center', value: 'create_time', sortable: true, width: 80},
        ]
      }
    },
    getName() {
      if (this.clinicUser) {
        return this.user.clinic_name
      } else if (this.doctorUser) {
        return this.user.doctor_name + this.$t('Navigation.doctor')
      }
    },
  },

  watch: {
    options: {
      handler() {
        this.getMembers()
      },
      deep: true
    }
  },

  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    searchMembers(){
      if (this.page === 1)
        return this.getMembers()
      this.page = 1
    },
    getMembers() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const params = {
        ...this.axiosParamData,
        page,
        limit: itemsPerPage,
        name: this.search || null
      }

      if (sortBy.length) {
        params.sort = sortBy[0]
        params.sort_type = sortDesc[0] ? 'desc' : 'asc'
      }

      let api

      if (this.memberType === 'line')
        api = getLineMembersAPI
      else if (this.memberType === 'messenger')
        api = getMessengerMembersAPI

      api(params).then(res => {
        this.totalMembers = parseInt(res.data.paginate.total)
        this.members = res.data.data
      }).catch(e => {
        alert(this.$t('GENERAL.oops'))
        console.error(e)
      }).finally(() => {
        this.loading = false
      })
    },
    showBindStatus(config) {
      const assetConfig = bindStatus(config)
      return {
        icon: assetURL(assetConfig.icon),
        text: assetConfig.text
      }
    },
    exportMembers() {
      let api

      if (this.memberType === 'line')
        api = exportLineMembersAPI
      else if (this.memberType === 'messenger')
        api = exportMessengerMembersAPI

      api(this.axiosParamData).then(res => {
        const blob = new Blob([res.data], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        const fileName = res.headers['content-disposition'].split("filename=")[1] || '會員列表'
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
    },
    async getSendChannelInfo(event, on) {  // 注意： 我跟其他人很不一樣 不要複製我
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }
      const isBind = Number(this.selectPatient.bind_status)

      const [smsQuotaRes] = await Promise.all([
        getSMSQuotaChannelInfoAPI(this.axiosParamData).catch(err => err.response)
      ])

      const smsQuota = smsQuotaRes.data?.quota
      this.sendSmsSuffix = ` - 剩餘${smsQuota}點`


      if (isBind) {
        const res3 = await getChannelQuotaAPI(this.axiosParamData)
        const lineQuota = Number(res3.data.quota) - Number(res3.data.total_usage)

        this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
      } else {
        this.sendLineSuffix = ' - 未綁定'
      }

      await on.click(event)
    }
  }
}
</script>

<style scoped>

</style>
